/**
 * Bank Book Screen - Using Report Engine
 * Standardized layout with database-driven columns, export, and column settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Landmark, Calendar, ArrowUpRight, ArrowDownRight, Search } from 'lucide-react';
import { ReportLayout } from '@/components/report-engine';
import api from '@/services/api';

const BankBookScreen = ({ theme }) => {
  const isDark = theme === 'dark';
  
  // Data state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [fromDate, setFromDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Summary state
  const [openingBalance, setOpeningBalance] = useState(0);
  const [totalReceipts, setTotalReceipts] = useState(0);
  const [totalPayments, setTotalPayments] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);

  // Load bank accounts on mount
  useEffect(() => {
    fetchBankAccounts();
  }, []);

  // Fetch bank book when bank or date changes
  useEffect(() => {
    if (selectedBank) {
      fetchBankBook();
    }
  }, [selectedBank, fromDate, toDate, page, pageSize]);

  const fetchBankAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await api.get('/api/vouchers/banks');
      if (response.data.success) {
        setBankAccounts(response.data.data || []);
        if (response.data.data?.length > 0) {
          setSelectedBank(response.data.data[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchBankBook = async () => {
    if (!selectedBank) return;
    
    setLoading(true);
    setError(null);
    try {
      // Use BANK_BOOK report code with server-side pagination
      const response = await api.post('/api/report-engine/data/BANK_BOOK', {
        page,
        pageSize,
        filters: { fromDate, toDate, accountId: selectedBank.GLId, search: searchQuery || undefined }
      });

      if (response.data.success) {
        const rows = response.data.data?.rows || [];
        const paginationData = response.data.data;
        
        const processedData = rows.map(r => ({
          ...r,
          DocDate: r.DocDateFormatted || r.DocDate,
          AccountName: r.AccountName,
          ChequeNo: r.Chequeno,
          Narration: r.CommNarration,
          Receipt: parseFloat(r.DrAmount) || 0,
          Payment: 0,
          Balance: 0
        }));

        let runningBalance = openingBalance;
        processedData.forEach(row => {
          runningBalance += row.Receipt - row.Payment;
          row.Balance = runningBalance;
        });

        setData(processedData);
        setTotalRows(paginationData.total || 0);
        setTotalPages(paginationData.totalPages || 0);

        const totReceipts = processedData.reduce((sum, r) => sum + r.Receipt, 0);
        const totPayments = processedData.reduce((sum, r) => sum + r.Payment, 0);
        setTotalReceipts(totReceipts);
        setTotalPayments(totPayments);
        setClosingBalance(openingBalance + totReceipts - totPayments);
      }
    } catch (err) {
      console.error('Error fetching bank book:', err);
      setError('Failed to load bank book data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(() => {
    fetchBankBook();
  }, [selectedBank, fromDate, toDate, page, pageSize]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Filter data by search query
  const filteredData = data.filter(row =>
    !searchQuery ||
    row.DocId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    row.AccountName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    row.Chequeno?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Summary cards
  const summaryCards = selectedBank ? [
    { 
      label: 'Opening Balance', 
      value: formatCurrency(openingBalance),
      icon: <Landmark size={16} />
    },
    { 
      label: 'Total Receipts', 
      value: formatCurrency(totalReceipts), 
      variant: 'success',
      icon: <ArrowUpRight size={16} />
    },
    { 
      label: 'Total Payments', 
      value: formatCurrency(totalPayments), 
      variant: 'danger',
      icon: <ArrowDownRight size={16} />
    },
    { 
      label: 'Closing Balance', 
      value: formatCurrency(closingBalance),
      variant: closingBalance >= 0 ? 'success' : 'danger',
      icon: <Landmark size={16} />
    }
  ] : [];

  // Toolbar filters
  const FiltersToolbar = (
    <div className="flex flex-wrap items-center gap-3">
      {/* Bank Selector */}
      <select
        value={selectedBank?.GLId || ''}
        onChange={(e) => {
          const bank = bankAccounts.find(b => b.GLId === e.target.value);
          setSelectedBank(bank);
        }}
        data-testid="bankbook-bank-select"
        className={`px-3 py-1.5 rounded-lg border text-sm min-w-48 ${
          isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'
        }`}
      >
        <option value="">Select Bank Account</option>
        {bankAccounts.map(bank => (
          <option key={bank.GLId} value={bank.GLId}>{bank.GLName}</option>
        ))}
      </select>

      {/* Date Range */}
      <div className="flex items-center gap-2">
        <Calendar size={16} className={isDark ? 'text-slate-500' : 'text-gray-400'} />
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          data-testid="bankbook-from-date"
          className={`px-3 py-1.5 rounded-lg border text-sm ${
            isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}
        />
        <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          data-testid="bankbook-to-date"
          className={`px-3 py-1.5 rounded-lg border text-sm ${
            isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}
        />
      </div>
      
      {/* Search */}
      <input
        type="text"
        placeholder="Search voucher, cheque..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        data-testid="bankbook-search"
        className={`px-3 py-1.5 rounded-lg border text-sm w-44 ${
          isDark ? 'bg-[#151515] border-[#333] text-white placeholder:text-slate-600' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
        }`}
      />
    </div>
  );

  // Show bank selection prompt if no bank selected
  if (!selectedBank && !accountsLoading) {
    return (
      <ReportLayout
        reportCode="BANK_BOOK"
        reportTitle="Bank Book"
        reportSubtitle="Select a bank account to view transactions"
        reportIcon={<Landmark size={20} />}
        data={[]}
        loading={accountsLoading}
        theme={theme}
        filters={FiltersToolbar}
      >
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Landmark className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-slate-700' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Select a Bank Account
            </h3>
            <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              Choose a bank account from the dropdown to view its transactions
            </p>
          </div>
        </div>
      </ReportLayout>
    );
  }

  return (
    <ReportLayout
      reportCode="BANK_BOOK"
      reportTitle="Bank Book"
      reportSubtitle={selectedBank ? `${selectedBank.GLName} | ${new Date(fromDate).toLocaleDateString('en-IN')} to ${new Date(toDate).toLocaleDateString('en-IN')}` : 'Select a bank account'}
      reportIcon={<Landmark size={20} />}
      data={filteredData}
      loading={loading || accountsLoading}
      error={error}
      onRefresh={handleRefresh}
      theme={theme}
      summaryCards={summaryCards}
      filters={FiltersToolbar}
      showRowNumbers={true}
      pagination={{ page, pageSize, total: totalRows, totalPages }}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
    />
  );
};

export default BankBookScreen;
